import type { PropsWithChildren } from "react";

import { styled } from "@linaria/react";
import Tippy from "@tippyjs/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import Text, { TextSize } from "../text/Text";

export enum TooltipPosition {
  TOP = "top",
  BOTTOM = "bottom",
  LEFT = "left",
  RIGHT = "right",
  TOP_START = "top-start",
  TOP_END = "top-end",
  BOTTOM_START = "bottom-start",
  BOTTOM_END = "bottom-end",
  RIGHT_START = "right-start",
  RIGHT_END = "right-end",
  LEFT_START = "left-start",
  LEFT_END = "left-end",
  AUTO = "auto",
  AUTO_START = "auto-start",
  AUTO_END = "auto-end",
}

export enum TooltipVariant {
  BASE = "BASE",
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
}

interface TooltipProps {
  body: string | React.ReactNode;
  variant?: TooltipVariant;
  position?: TooltipPosition;
  delay?: number;
  offset?: [number, number];
  isInteractive?: boolean;
  noPadding?: boolean;
  noBorder?: boolean;
  isDisabled?: boolean;
}

interface TooltipContentProps {
  $variant: TooltipVariant;
  $noPadding: boolean;
  $noBorder: boolean;
}

const TooltipWrapper = styled.span`
  display: flex;
  align-items: center;
`;

const TooltipContent = withTheme(styled.span<PropsWithTheme<TooltipContentProps>>`
  display: inline-block;
  width: fit-content;
  max-width: max-content;
  padding: ${({ $noPadding }) => ($noPadding ? "0" : "8px")};
  background-color: ${({ $variant, theme }) => {
    return match($variant)
      .with(TooltipVariant.BASE, () => theme.color.background.base)
      .with(TooltipVariant.PRIMARY, () => theme.color.background.primary)
      .with(TooltipVariant.SECONDARY, () => theme.color.background.secondary)
      .with(TooltipVariant.TERTIARY, () => theme.color.background.tertiary)
      .exhaustive();
  }};
  border: ${({ $noBorder, $variant, theme }) => {
    if ($noBorder) {
      return "none";
    }

    return match($variant)
      .with(TooltipVariant.BASE, () => `0.5px solid ${theme.color.border.secondary}`)
      .with(TooltipVariant.PRIMARY, () => `0.5px solid ${theme.color.border.secondary}`)
      .with(TooltipVariant.SECONDARY, () => `0.5px solid ${theme.color.border.secondary}`)
      .with(TooltipVariant.TERTIARY, () => `0.5px solid ${theme.color.border.secondary}`)
      .exhaustive();
  }};
  border-radius: 5px;
`);

const Tooltip = ({
  children,
  variant = TooltipVariant.SECONDARY,
  body,
  position = TooltipPosition.TOP,
  delay = 0,
  offset = [0, 4],
  isInteractive = false,
  noPadding = false,
  noBorder = false,
  isDisabled = false,
}: PropsWithChildren<TooltipProps>) => {
  if (isDisabled) {
    return children;
  }

  const content = (
    <TooltipContent $variant={variant} $noPadding={noPadding} $noBorder={noBorder}>
      {typeof body === "string" ? <Text size={TextSize.BODY_SM}>{body}</Text> : body}
    </TooltipContent>
  );

  return (
    <Tippy
      content={content}
      placement={position}
      delay={delay}
      duration={0}
      offset={offset}
      interactive={isInteractive}
      zIndex={10000} // Above modals (9000-9001) - see z-index scale
    >
      <TooltipWrapper>{children}</TooltipWrapper>
    </Tippy>
  );
};

export default Tooltip;
